import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip as WmsTooltip } from "react-tooltip";
import { getAllTaskPriority, getAllTaskStatus, getAllTaskType, createTask, getTaskById, updateTaskAttachments, updateTask, handleDownloadFile, handleDownloadAllFiles } from '../../store/actions/picker.action';
import { getAllCompanyDetails } from '../../store/actions/rateCard';
import { getSellersforMultipleCompany } from '../../store/actions/company';
import Select from 'react-select';
import { toast } from "react-toastify";
import { allowedDocExtensions } from '../../constant';
import _, { set } from 'lodash';
import ChargeSeller from './ChargeSeller';
import { findConfiguration, getSelectList } from '../../config/utils';
import PdfMergePopup from '../common/PDFMerge';
import LoaderSpinner from '../common/Loader';
import DatePickerComponent from '../common/DatePickerComponent';
import InlineFilePreview from '../common/PreviewAttachment';
import ViewImage from '../common/ViewImage';
import ChargesHistory from '../CarrierConnect/ChargesHistory';
import { Tooltip } from 'react-tooltip';
import AlertDialogModal from '../common/AlertDialog';

/*istanbul ignore next */
const Tasks = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    let dispatch = useDispatch();
    const location = useLocation();
    const userDetails = useSelector((state) => state.auth.user);
    const [isEditable, setIsEditable] = useState(location?.state?.editable);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageRecord, setSelectedImageRecord] = useState({});
    const configurations = useSelector((state) => state?.auth?.configurations);
    const [maxSize, setMaxSize] = useState(0);
    const [showLoader, setShowLoader] = useState(false);
    const [showPopUp, setShowPopUp] = useState(false);
    const [formReset, setFormReset] = useState([]);
    const [showCharge, setShowCharge] = useState(false);
    const [showChargeAlert, setShowChargeAlert] = useState(false);
    const [formData, setFormData] = useState({
        taskTypeId: 0,
        taskPriorityId: 0,
        taskTitle: '',
        taskStatusId: 1,
        sku: '',
        quantity: '',
        instructions: '',
        attachments: [],
        amount: 0,
        companyId: 0,
        sellerId: 0,
        entryDate: new Date(),
    });
    const [errors, setErrors] = useState({});
    const [showChargesDetails, setShowChargesDetails] = useState(false);
    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const { allTaskPriorityList, allTaskStatusList, allTaskTypeList } = useSelector((state) => state.pickerReducer);
    const { getAllCompany } = useSelector((state) => state.RateCardReducer);
    const getSellersValue = useSelector((state) => state.company?.getMultipleCompanySellers);
    const MAX_FILE_COUNT = 10;

    useEffect(() => {
        if (configurations && configurations.length > 0) {
            const maxSizeConfig = findConfiguration('TASK_ATTACHMENT_SIZE', configurations);
            if (maxSizeConfig) {
                setMaxSize(maxSizeConfig);
            }
        }
    }, [configurations]);

    useEffect(() => {
        if (Number(id) > 0) {
            fetchTaskDetails();
        }
    }, [id]);

    // When task details are loaded for an existing task, ensure seller list is populated
    // so the specific seller can be pre-selected (super-admin case)
    useEffect(() => {
        if (Number(id) > 0 && userDetails?.Value?.isSuperAdmin) {
            const taskCompanyId = Number(formData.companyId) || 0;
            if (taskCompanyId > 0) {
                // fetch sellers for this company to ensure sellerList contains the task seller
                dispatch(getSellersforMultipleCompany([taskCompanyId]));
            }
        }
    }, [formData.companyId, id, userDetails]);

    useEffect(() => {
        if (!allTaskPriorityList?.length) {
            dispatch(getAllTaskPriority());
        }

        if (!allTaskStatusList?.length) {
            dispatch(getAllTaskStatus());
        }

        if (!allTaskTypeList?.length) {
            dispatch(getAllTaskType());
        }
    }, []);

    useEffect(() => {
        if (userDetails?.Value?.isSuperAdmin) {
            // Fetch full company list (same as Top Up)
            dispatch(getAllCompanyDetails(null, 500, 1));
            // If create page, fetch all sellers (companyId 0 = all)
            if (Number(id) === 0) {
                dispatch(getSellersforMultipleCompany([0]));
            }
        }
    }, [userDetails, id]);

    // When company selection changes (for super admin create flow), fetch sellers for that company
    useEffect(() => {
        if (userDetails?.Value?.isSuperAdmin && Number(id) === 0) {
            const selectedCompanyId = Number(formData.companyId) || 0;
            dispatch(getSellersforMultipleCompany([selectedCompanyId]));
            // Reset seller selection when company changes
            setFormData(prev => ({ ...prev, sellerId: 0 }));
        }
    }, [formData.companyId, userDetails, id]);

    // Populate company and seller lists from Redux state (use Top Up data shapes)
    useEffect(() => {
        let companyData = getAllCompany?.Entity?.listCompanyMasterOrdersEntity || [];
        if (companyData.length) {
            // map to { value, label }
            setCompanyList(getSelectList(companyData, 'Id', 'name'));
        }

        let sellerData = getSellersValue?.Entity?.listSellerMasterOrdersEntity || [];
        if (sellerData.length) {
            setSellerList(getSelectList(sellerData, 'id', 'sellerAndCompany'));
        } else {
            setSellerList([]);
        }
    }, [getAllCompany, getSellersValue]);

    // Validation helpers extracted to reduce cognitive complexity
    const isNonZero = (v) => Number(v) && Number(v) !== 0;
    const isNonEmptyString = (v) => typeof v === 'string' && v.trim() !== '';

    const validateAmountForTaskType = (formData, errors) => {
        if (Number(formData.taskTypeId) === 4 && (!formData.amount || Number(formData.amount) <= 0)) {
            errors.amount = 'Amount is required for this task type';
        }
    };

    const validateCompanySellerForSuperAdmin = (formData, errors) => {
        const isSuperAdminCreate = userDetails?.Value?.isSuperAdmin && Number(id) === 0;
        if (!isSuperAdminCreate) return;

        if (!formData.companyId || Number(formData.companyId) === 0) {
            errors.companyId = 'Company is required';
        }
        if (!formData.sellerId || Number(formData.sellerId) === 0) {
            errors.sellerId = 'Seller is required';
        }
    };

    const validateForm = (formData) => {
        const errors = {};
        // Required field checks
        const requiredChecks = [
            { key: 'taskTypeId', ok: isNonZero, msg: 'Task Type is required' },
            { key: 'taskPriorityId', ok: isNonZero, msg: 'Task Priority is required' },
            { key: 'taskTitle', ok: isNonEmptyString, msg: 'Task Title is required' },
            { key: 'taskStatusId', ok: isNonZero, msg: 'Task Status is required' },
        ];

        requiredChecks.forEach(({ key, ok, msg }) => {
            if (!ok(formData[key])) {
                errors[key] = msg;
            }
        });

        // Conditional validations
        validateAmountForTaskType(formData, errors);
        validateCompanySellerForSuperAdmin(formData, errors);

        setErrors(errors);
        return errors;
    };

    const openChargeSellerDialog = async () => {
        if (formData?.postCharges?.length > 0) {
            setShowChargeAlert(true);
        } else {
            setShowCharge(!showCharge);
        }
    };

    // Helper function to prepare task data for submission
    const prepareTaskData = () => {
        if (Number(id) > 0) {
            return { ...formData, isActive: true };
        }

        const baseData = { ...formData };
        if (userDetails?.Value?.isSuperAdmin) {
            baseData.taskForCompanyId = formData.companyId;
            baseData.taskForSellerId = formData.sellerId;
        }
        return baseData;
    };

    // Helper function to submit task (create or update)
    const submitTaskData = async (taskData) => {
        const isUpdate = Number(id) > 0;
        return isUpdate ? updateTask(taskData) : createTask(taskData);
    };

    // Helper function to handle success response
    const handleTaskSuccess = (response) => {
        const newAttachments = formData.attachments.filter(file => file.isNew);
        const hasNewAttachments = newAttachments.length > 0;

        if (hasNewAttachments) {
            const taskId = Number(id) > 0 ? id : response.data.Entity;
            handleSaveAttachments(taskId);
            return;
        }

        setShowLoader(false);
        const isUpdate = Number(id) > 0;
        const message = isUpdate ? 'Task Updated successfully!' : 'Task created successfully!';
        toast.success(message);
        navigate('/tasks');
    };

    // Helper function to extract error message from response
    const getErrorMessage = (response) => {
        return response?.data?.Message?.AppStatusDescription ||
            response?.data?.Message ||
            'Failed to create/update task. Please try again.';
    };

    // Helper function to check if response indicates success
    const isSuccessResponse = (response) => {
        return response?.data?.Status === 200;
    };

    const handleSaveTask = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length > 0) {
            console.log('Validation Errors:', errors);
            return;
        }

        try {
            setShowLoader(true);
            const taskData = prepareTaskData();
            const response = await submitTaskData(taskData);

            if (isSuccessResponse(response)) {
                handleTaskSuccess(response);
            } else {
                setShowLoader(false);
                const apiErrorMessage = getErrorMessage(response);
                toast.error(apiErrorMessage);
            }
        } catch (error) {
            setShowLoader(false);
            toast.error('An error occurred while creating the task.');
        }
    };

    const handleCancelPdfPopUp = () => {
        setShowPopUp(!showPopUp);
    };

    const handleUploadPdfAction = (file, fileDetail) => {
        handleCancelPdfPopUp();
    }

    const handleSaveAttachments = async (taskId) => {
        const formDataToSend = new FormData();
        let newFiles = formData.attachments = formData.attachments.filter(file => file.isNew);
        newFiles.forEach(file => {
            formDataToSend.append('files', file);
        });
        formDataToSend.append('TaskId', taskId);

        try {
            let response = await updateTaskAttachments(formDataToSend);
            if (response.status === 200) {
                setShowLoader(false);
                toast.success(Number(id) > 0 ? 'Task Updated successfully!' : 'Task created successfully!');
                navigate('/tasks');
            } else {
                setShowLoader(false);
                toast.error('Failed to save attachments. Please try again.');
            }
        } catch (error) {
            setShowLoader(false);
            toast.error('An error occurred while saving attachments.');
        }
    }

    const handleResetForm = () => {
        if (Number(id) > 0) {
            fetchTaskDetails();
        } else {
            setFormData({
                taskTypeId: 0,
                taskPriorityId: 0,
                taskTitle: '',
                taskStatusId: 0,
                sku: '',
                quantity: '',
                instructions: '',
                attachments: [],
                amount: 0,
                companyId: 0,
                sellerId: 0,
            });
        }
        setFormReset(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        setErrors({
            ...errors,
            [name]: '', // Clear error for the field being edited
        });
        setFormReset(false);
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value.trim(),
        });
    }

    const fetchTaskDetails = async () => {
        setShowLoader(true);
        let res = await getTaskById(id);
        if (res.data.Status === 200) {
            setIsEditable(!location?.state?.editable ? false : res.data.Entity.isEditable);
            setShowLoader(false);
            let obj = {
                ...res.data.Entity,
                taskId: res.data.Entity.id,
            }
            setFormData(obj);
        } else {
            setShowLoader(false);
            console.log('Error Log:', res.data);
        }
    };

    const handleDeleteAttachment = (item, index) => {
        let formDataClone = _.cloneDeep(formData);
        if (item.isNew) {
            formDataClone.attachments.splice(index, 1);
        } else {
            formDataClone.attachments[index].isDeleted = true;
            formDataClone.attachmentActions = formDataClone.attachmentActions ?? [];
            formDataClone.attachmentActions.push(formDataClone.attachments[index]);
            formDataClone.attachments.splice(index, 1);
        }
        setFormData(formDataClone);
    };

    const handleDownload = async (item) => {
        let response = await handleDownloadFile(encodeURIComponent(item.fileUrl));
        if (response.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', item.name);
            document.body.appendChild(link); // NOSONAR
            link.click();
            document.body.removeChild(link); // NOSONAR
        } else {
            toast.error('Failed to download file.');
        }
    }

    const handleAttachments = (e) => {
        let files = [...e.target.files];
        try {
            if (files.length === 0) return;
            if (files.length > MAX_FILE_COUNT) {
                toast.info(`Only ${MAX_FILE_COUNT} files can be uploaded at one time`, 1000);
                e.target.value = "";
                return;
            }

            // Validate extensions and individual file size
            let validFiles = [];
            let totalSizeBytes = 0;

            files.forEach((file) => {
                const extension = file.name.split(".").pop()?.toLowerCase();
                const fileSizeBytes = file.size;

                if (!allowedDocExtensions.includes(extension)) {
                    toast.error(`Unsupported file format: ${file.name}`, 1000);
                    return;
                }
                totalSizeBytes += fileSizeBytes;
                file.isNew = true;
                validFiles.push(file);
            });

            // Check total size
            if (totalSizeBytes > maxSize * 1024 * 1024) {
                toast.info(`Total file size should not exceed ${maxSize}MB`, 1000);
                e.target.value = "";
                return;
            }

            if (validFiles.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    attachments: [...prev.attachments, ...validFiles],
                }));
            }

            e.target.value = ""; // Reset input
        } catch (err) {
            console.log(err);
        }
    };

    const handleEditRecord = () => {
        if (formData.isEditable) {
            setIsEditable(!isEditable);
        };
    };

    const handleDownloadAll = async () => {
        let oldAttachments = formData.attachments.filter(file => !file.isNew);
        let response = await handleDownloadAllFiles(oldAttachments);
        if (response.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const disposition = response.headers['content-disposition'];
            let filename = `Attachments_${new Date().toISOString()}.zip`;
            if (disposition && disposition.indexOf('filename=') !== -1) {
                filename = disposition
                    .split('filename=')[1]
                    .split(';')[0]
                    .replace(/"/g, ''); //NOSONAR
            }
            link.setAttribute('download', filename);
            document.body.appendChild(link); // NOSONAR
            link.click();
            document.body.removeChild(link); // NOSONAR
        } else {
            toast.error('Failed to download file.');
        }
    };

    const handleDateFieldChange = (date) => {
        setFormData({
            ...formData,
            entryDate: date
        });
    };

    const handleViewImage = (item) => {
        let data = {
            ...item,
            imageUrl: item.fileUrl
        }
        setSelectedImageRecord(data);
        setShowImageModal(true);
    };

    const handleChargesDetails = () => {
        setShowChargesDetails(!showChargesDetails);
    };

    const handleOverrideCharge = () => {
        setShowCharge(!showCharge);
        setShowChargeAlert(false);
    };

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>{Number(id) ? 'Update' : 'Create'} Task {Number(id) > 0 && `(# ${Number(id)})`} {formData?.id && formData?.postCharges?.length > 0 && <button id='chargesBtn' className='sortBtn fontFix' onClick={handleChargesDetails}><i className='fa fa-info-circle' aria-hidden="true" style={{color: "#ff0000"}}></i></button>}</span>

                    <div className="top_right_button_container">
                        <div className="top_right_button_container">    
                            {userDetails?.Value?.isSuperAdmin && Number(id) > 0 && (
                                <button
                                    className="btn cus-seconday-bg-btn top-btn"
                                    onClick={openChargeSellerDialog}
                                >
                                    Post Charges
                                </button>
                            )}
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleSaveTask}
                                disabled={!isEditable}
                            >
                                Save
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleResetForm}
                                disabled={!isEditable || formReset}
                            >
                                Reset Form
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleEditRecord}
                                disabled={!formData.isEditable || isEditable}
                            >
                                Edit
                            </button>
                            <button
                                className="btn cus-primary-transparent-btn bckbtn"
                                onClick={() => navigate('/tasks')}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
                {showLoader && <div className="full-screen-spinner-overlay zIndex2"><LoaderSpinner /></div>}
                <div className="incoming_wrapper main_content_table fontFix addressDetailWrapper">
                    <div className='row mb-3'>
                        <div className='col-lg-6 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Type <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskTypeId}
                                name="taskTypeId"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            >
                                <option value="0">Select Task Type</option>
                                {allTaskTypeList?.map((item, index) => (
                                    <option key={item.id} value={item.id}>{item.typeName}</option>
                                ))}
                            </select>
                            {errors.taskTypeId && <div className="requiredthis">*This is required</div>}
                        </div>
                        <div className='col-lg-6 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Priority <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskPriorityId}
                                name="taskPriorityId"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            >
                                <option value="0">Select Priority</option>
                                {allTaskPriorityList?.map((item, index) => (
                                    <option key={item.id} value={item.id}>{item.priorityName}</option>
                                ))}
                            </select>
                            {errors.taskPriorityId && <div className="requiredthis">*This is required</div>}
                        </div>
                    </div>
                    <div className='row mb-3'>
                        <div className={`col-lg-${userDetails?.Value?.isSuperAdmin ? '6' : '12'} col-md-12 col-sm-12 mobMb-10`}>
                            <div className="input-label-txt">Title <span className="require-field">*</span></div>
                            <input
                                className="form-control"
                                placeholder="Enter task title"
                                type="text"
                                value={formData.taskTitle || ''}
                                name="taskTitle"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                                onBlur={handleBlur}
                            />
                            {errors.taskTitle && <div className="requiredthis">*This is required</div>}
                        </div>
                        {userDetails?.Value?.isSuperAdmin && (
                            <>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Company <span className="require-field">*</span></div>
                                    <div className="select_react">
                                        <Select
                                            options={companyList}
                                            onChange={(opt) => {
                                                const val = opt?.value || 0;
                                                setFormData(prev => ({ ...prev, companyId: val, sellerId: 0 }));
                                                // fetch sellers for selected company (create flow)
                                                if (Number(id) === 0) {
                                                    dispatch(getSellersforMultipleCompany([val]));
                                                }
                                            }}
                                            className="activeSellerListInput"
                                            // Disable company select for existing tasks (shouldn't be editable)
                                            isDisabled={Number(id) > 0 || !isEditable}
                                            value={companyList?.find(c => c.value === formData.companyId) || null}
                                            placeholder="Select Company..."
                                            isMulti={false}
                                            classNamePrefix="list-value"
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    minHeight: '28px',
                                                    height: '36px',
                                                    fontSize: '13px',
                                                    paddingTop: '0.5px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: '#515558ff'
                                                })
                                            }}
                                        />
                                    </div>
                                    {errors.companyId && <div className="requiredthis">*This is required</div>}
                                </div>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Seller <span className="require-field">*</span></div>
                                    <div className="select_react">
                                        <Select
                                            options={sellerList}
                                            onChange={(opt) => setFormData(prev => ({ ...prev, sellerId: opt?.value || 0 }))}
                                            className="activeSellerListInput"
                                            // Disable seller select for existing tasks (shouldn't be editable)
                                            isDisabled={Number(id) > 0 || !isEditable}
                                            value={sellerList?.find(s => s.value === formData.sellerId) || null}
                                            placeholder="Select Seller..."
                                            isMulti={false}
                                            classNamePrefix="list-value"
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    minHeight: '28px',
                                                    height: '36px',
                                                    fontSize: '13px',
                                                    paddingTop: '0.5px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: '#515558ff'
                                                })
                                            }}
                                        />
                                    </div>
                                    {errors.sellerId && <div className="requiredthis">*This is required</div>}
                                </div>
                            </>
                        )}
                    </div>
                    <div className='row mb-3'>
                        <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Status <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskStatusId ?? 0}
                                name="taskStatusId"
                                onChange={handleInputChange}
                                disabled={!userDetails?.Value?.isSuperAdmin || !isEditable}
                            >
                                {allTaskStatusList?.map((item) => (
                                    <option key={item.id} value={item.id}>{item.statusName}</option>
                                ))}
                            </select>
                            {errors.taskStatusId && <div className="requiredthis">*This is required</div>}
                        </div>
                        {Number(formData.taskTypeId) === 4 ? (
                            <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                <div className="input-label-txt">Amount ($)</div>
                                <input
                                    className={`form-control`}
                                    type={"number"}
                                    name="amount"
                                    maxLength={10}
                                    placeholder="Enter Amount"
                                    value={formData.amount || ''}
                                    onChange={handleInputChange}
                                    disabled={!isEditable}
                                    onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                    onBlur={handleBlur}
                                    onScroll={(e) => e.target.blur()}
                                />
                                {errors.amount && <div className="requiredthis">*This is required</div>}
                            </div>
                        ) : (
                            <>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">SKU</div>
                                    <input
                                        className={`form-control outboundNumber`}
                                        type="text"
                                        name="sku"
                                        placeholder="Enter SKU"
                                        value={formData.sku || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        onBlur={handleBlur}
                                        maxLength={30}
                                    />
                                </div>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Quantity</div>
                                    <input
                                        className={`form-control outboundNumber`}
                                        type="number"
                                        name="quantity"
                                        placeholder="Enter Quantity"
                                        value={formData.quantity || ''}
                                        maxLength={10}
                                        onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        onBlur={handleBlur}
                                    />
                                </div>
                                <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                                    <div className="input-label-txt">Date</div>
                                    <DatePickerComponent
                                        dateField={formData.entryDate}
                                        handleDateChange={(date) => handleDateFieldChange(date)}
                                        fieldName="entryDate"
                                        dateFormat="MM-dd-yyyy"
                                        disabled={!isEditable || !userDetails?.Value?.isSuperAdmin}
                                        onKeyDown={(e) => {
                                            e.preventDefault();
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <div className='row mb-3'>
                        <div className="col col-lg-6 col-md-6 col-sm-12 mobMb-10">
                            <div className="input-label-txt">Attachments <i id="uploadInfo" className="fa fa-info-circle" aria-hidden="true"></i></div>
                            <WmsTooltip
                                anchorId={`uploadInfo`}
                                content={`Upload files to attach to this task. You can upload multiple files here. Max file size is ${maxSize}MB.`}
                                place="top"
                                className="tooltipTxt"
                            />
                            <div className="uploadContainer">
                                <div className="inlineBlock fileUpload inventory">
                                    <button
                                        type="button"
                                        className="btn cus-seconday-bg-btn"
                                        disabled={!isEditable}
                                    >
                                        Upload documents
                                    </button>
                                    <input
                                        className="hiddenFileField"
                                        type="file"
                                        id="customFile"
                                        multiple
                                        onChange={handleAttachments}
                                        disabled={!isEditable}
                                    />
                                </div>
                                <button className='ml-2 inlineBlock fileUpload inventory link sortBtn' onClick={handleCancelPdfPopUp}>
                                    Upload & Merge Pdf Files <i id="uploadMerge" className="fa fa-info-circle" aria-hidden="true"></i>
                                </button>
                                <WmsTooltip
                                    anchorId={`uploadMerge`}
                                    content={`Upload PDFs to combine, download the merge file, then re-upload it here.`}
                                    place="top"
                                    className="tooltipTxt"
                                />
                                <div>
                                    {formData.attachments.filter(item => item.fileUrl).length > 0 &&
                                        <button className='btn mt-1 mr-2 downloadAll' onClick={handleDownloadAll}><i className="fa fa-download" aria-hidden="true"></i> Download All</button>
                                    }
                                </div>
                                <div className='displayFlex attachmentsFlex'>
                                    {formData.attachments.map((item, index) => (
                                        <Fragment key={item?.id}>
                                            {!item.isDeleted && item.fileUrl && (
                                                <div className={`selectedFile mr-2`} key={item?.id}>
                                                    <div className="fileName">
                                                        {/\.(jpg|jpeg|png|gif|webp)$/i.test(item.name) ? (
                                                            <button className='sortBtn imagePreview' onClick={() => handleViewImage(item)}>
                                                                <img
                                                                src={item.fileUrl}
                                                                alt={item.name}
                                                                style={{ width: "100px", height: "auto", borderRadius: "8px" }}
                                                                />
                                                            </button>
                                                        ) : (
                                                            <a
                                                            href={item.fileUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            >
                                                            {item.name}
                                                            </a>
                                                        )}
                                                    </div>
                                                    {isEditable && (
                                                        <button
                                                            className="deleteFile"
                                                            onClick={() =>
                                                                handleDeleteAttachment(item, index)
                                                            }
                                                            disabled={!isEditable}
                                                        >
                                                            <i className="fa fa-times" aria-hidden="true"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        className="downBtn ml-2"
                                                        onClick={() =>
                                                            handleDownload(item)
                                                        }
                                                    >
                                                        <i className="fa fa-download" aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                                <div className='displayFlex alignItemsCenter flexWrap wrapTasks'>
                                    {formData.attachments.map((item, index) => (
                                        <Fragment key={item?.id}>
                                            {!item.fileUrl && (
                                                <div className={`selectedFile mr-2 filePreview`} key={item?.id}>
                                                    <div className="fileName">
                                                        <div>
                                                            {item.name}
                                                            <InlineFilePreview
                                                                file={item}
                                                                onClose={() => handleDeleteAttachment(item, index)}
                                                            />
                                                        </div>
                                                    </div>
                                                    {isEditable && item.fileUrl && (
                                                        <button
                                                            className="deleteFile"
                                                            onClick={() => handleDeleteAttachment(item, index)}
                                                            disabled={!isEditable}
                                                        >
                                                            <i className="fa fa-times" aria-hidden="true"></i>
                                                        </button>
                                                    )}
                                                    {item.fileUrl && (
                                                        <button
                                                            className="downBtn ml-2"
                                                            onClick={() => handleDownload(item)}
                                                        >
                                                            <i className="fa fa-download" aria-hidden="true"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='row mb-3'>
                        <div className='col-lg-12 col-md-12 col-sm-12 mobMb-10'>
                            <div className="input-label-txt" htmlFor="instructions">Comment</div>
                            <textarea
                                onBlur={handleBlur}
                                className="form-control"
                                placeholder="Enter any comment"
                                rows="7"
                                value={formData?.instructions}
                                name="instructions"
                                id="instructions"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>
            {showCharge && (
                <ChargeSeller show={showCharge} taskId={id} handleCancel={() => setShowCharge(false)} fetchTaskDetails={fetchTaskDetails}/>
            )}
            {showPopUp && (
                <PdfMergePopup handleCancel={handleCancelPdfPopUp} showPopUp={showPopUp} handleUploadPdfAction={handleUploadPdfAction} />
            )}

            {showImageModal && (
                <ViewImage
                    selectedRecord={selectedImageRecord}
                    showHideImageModal={showImageModal}
                    setShowHideImageModal={setShowImageModal}
                    title="Preview"
                />
            )}

            {showChargesDetails && (
                <ChargesHistory
                    show={showChargesDetails} 
                    handleCancel={() => setShowChargesDetails(false)} 
                    data={formData}
                />
            )}
            <Tooltip anchorId="chargesBtn" place="right" className="tooltipWidth fontFix">
                <div>Show Charges History</div>
            </Tooltip>

            <AlertDialogModal
                modalShow={showChargeAlert}
                handleCancelDeleteAlert={() => setShowChargeAlert(false)}
                modalClass={"modalDeleteWrapper"}
                messageText="This task has already been charged. Do you want to charge it again?"
                handleYesDeleteAlert={handleOverrideCharge}
                headingText={"POST CHARGES"}
            />
        </main >
    );
}

export default Tasks;
